const RUNTIME_FILES = [
  { source: 'export-runtime/index.html', target: 'index.html' },
  { source: 'export-runtime/viewer.js', target: 'viewer.js' },
  { source: 'export-runtime/viewer.css', target: 'viewer.css' },
  { source: 'favicon.ico', target: 'favicon.ico' },
];

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'bmp']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'mp4']);

function sourceFileName(sourceName, sourceUrl, fallback) {
  const value = String(sourceName || sourceUrl || fallback).split('?')[0].split('#')[0];
  return value.split('/').pop() || fallback;
}

function extensionForMedia(sourceName, sourceUrl, mimeType, kind) {
  const fileName = sourceFileName(sourceName, sourceUrl, 'asset');
  const match = fileName.match(/\.([a-z0-9]+)$/i);
  const extension = match?.[1]?.toLowerCase();
  const allowed = kind === 'panos' ? IMAGE_EXTENSIONS : AUDIO_EXTENSIONS;
  if (extension && allowed.has(extension)) return extension;

  const mimeMap = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/avif': 'avif',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
  };
  return mimeMap[mimeType] || (kind === 'panos' ? 'jpg' : 'mp3');
}

async function toBlob(source) {
  if (source instanceof Blob) return source;
  const response = await fetch(source);
  if (!response.ok) throw new Error(`无法读取导出素材（${response.status}）`);
  return response.blob();
}

async function addRuntimeFiles(zip) {
  const results = await Promise.all(
    RUNTIME_FILES.map(async (file) => {
      const response = await fetch(new URL(file.source, document.baseURI));
      if (!response.ok) throw new Error(`播放器运行文件缺失：${file.source}`);
      return { target: file.target, content: await response.blob() };
    })
  );
  results.forEach(({ target, content }) => zip.file(target, content));
}

function createDeploymentReadme() {
  return [
    'VR tour deployment package',
    '',
    '1. Extract every file in this archive into one empty web directory.',
    '2. Publish that directory with Nginx, Apache, or object storage static hosting.',
    '3. Open the directory URL with a trailing slash, for example:',
    '   https://example.com/vr-tours/my-tour/',
    '',
    'Do not move index.html, viewer.js, viewer.css, tour.json, or assets separately.',
    'All media paths are relative to this directory.',
  ].join('\n');
}

function timestamp() {
  return new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').replace('Z', '');
}

export async function createTourPackage({ panos, currentId, stationsByPano, onProgress }) {
  if (!Array.isArray(panos) || panos.length === 0) {
    throw new Error('请先添加至少一张全景图');
  }

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const mediaTasks = new Map();
  let panoIndex = 0;
  let audioIndex = 0;

  const addMedia = async ({ kind, sourceFile, sourceUrl, sourceName }) => {
    if (!sourceFile && !sourceUrl) return null;
    const key = sourceFile || sourceUrl;
    const cached = mediaTasks.get(key);
    if (cached) return cached;

    const task = (async () => {
      const blob = await toBlob(sourceFile || sourceUrl);
      const extension = extensionForMedia(sourceName, sourceUrl, blob.type, kind);
      const sequence = kind === 'panos' ? ++panoIndex : ++audioIndex;
      const path = `assets/${kind}/${kind === 'panos' ? 'pano' : 'audio'}-${sequence}.${extension}`;
      zip.file(path, blob, { compression: 'STORE' });
      return `./${path}`;
    })();
    mediaTasks.set(key, task);
    return task;
  };

  onProgress?.('正在整理全景图片...');
  const exportedPanos = await Promise.all(
    panos.map(async (pano) => ({
      id: pano.id,
      name: pano.name,
      src: await addMedia({
        kind: 'panos',
        sourceFile: pano.sourceFile,
        sourceUrl: pano.url,
        sourceName: pano.sourceName || pano.name,
      }),
      entryAudioSrc: await addMedia({
        kind: 'audio',
        sourceFile: pano.entryAudioFile,
        sourceUrl: pano.entryAudioUrl,
        sourceName: pano.entryAudioSourceName || pano.entryAudioName,
      }),
    }))
  );

  onProgress?.('正在整理语音和导航点...');
  const exportedStationsByPano = Object.fromEntries(
    await Promise.all(
      Object.entries(stationsByPano || {}).map(async ([panoId, stations]) => [
        panoId,
        await Promise.all(
          (stations || []).map(async (station) => ({
            id: station.id,
            name: station.name,
            yaw: station.yaw,
            pitch: station.pitch,
            targetPanoId: station.targetPanoId,
            audioSrc: await addMedia({
              kind: 'audio',
              sourceFile: station.audioFile,
              sourceUrl: station.audioUrl,
              sourceName: station.audioSourceName || station.audioName,
            }),
          }))
        ),
      ])
    )
  );

  const startPanoId = exportedPanos.some((pano) => pano.id === currentId)
    ? currentId
    : exportedPanos[0].id;
  const manifest = {
    format: 'vr-tour-export',
    version: 1,
    createdAt: new Date().toISOString(),
    startPanoId,
    panos: exportedPanos,
    stationsByPano: exportedStationsByPano,
  };

  onProgress?.('正在加入播放器...');
  await addRuntimeFiles(zip);
  zip.file('tour.json', JSON.stringify(manifest, null, 2));
  zip.file('README.txt', createDeploymentReadme());

  onProgress?.('正在生成 ZIP 文件...');
  const blob = await zip.generateAsync(
    { type: 'blob', compression: 'STORE' },
    (metadata) => onProgress?.(`正在生成 ZIP 文件... ${Math.round(metadata.percent)}%`)
  );

  return {
    blob,
    fileName: `vr-tour-${timestamp()}.zip`,
    manifest,
  };
}

export function downloadTourPackage(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
